import { DateTime } from 'luxon';
import { toSignal } from '@angular/core/rxjs-interop';
import { SessionService } from '../../services/session.service';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  inject,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { BucketService, toBucketUpdateDraft } from '../../services/bucket.service';
import { MonthService } from '../../services/month.service';
import { Bucket } from '../../types';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { AnalyticsService } from '../../services/analytics.service';
import { EVENT_DELETED_BUDGET, EVENT_UPDATED_BUDGET } from '../../analytics/events';
import { ArchiveBucketDialogComponent } from '../../bucket/archive-bucket-dialog/archive-bucket-dialog.component';
import { Subscription } from 'rxjs';
import { UiDialogService } from '../../ui/dialog/dialog.service';
import { UiFieldComponent } from '../../ui/field/field.component';
import { UiCurrencyInputComponent } from '../../ui/currency-input/currency-input.component';
import { UiInputDirective } from '../../ui/input/input.directive';
import { AsyncPipe } from '@angular/common';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { BucketTypeSelectorComponent } from '../bucket-type-selector/bucket-type-selector.component';
import {
  bucketTypeAmountLabel,
  bucketTypeGoalLabel,
  bucketTypeShowsGoal,
  BucketTypeOptionValue,
} from '../bucket-type-options';
import { CreateBucketRequest } from '../../generated-api/models';
import { UiCheckboxComponent } from '../../ui/checkbox/checkbox.component';
import { UiSaveButtonComponent, UiSaveButtonState } from '../../ui/save-button/save-button.component';

interface EditBucketFormValue {
  goal: number | null;
  name: string;
  amount: number | null;
  type: BucketTypeOptionValue;
  excludedFromSpendingReports: boolean;
}

@Component({
  selector: 'app-edit-bucket',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './edit-bucket.component.html',
  imports: [
    ReactiveFormsModule,
    UiFieldComponent,
    UiCurrencyInputComponent,
    UiInputDirective,
    UiButtonDirective,
    AsyncPipe,
    BucketTypeSelectorComponent,
    UiCheckboxComponent,
    UiSaveButtonComponent,
  ],
})
export class EditBucketComponent implements OnInit, OnDestroy, OnChanges {
  private analytics = inject(AnalyticsService);
  private bucketService = inject(BucketService);
  private dialog = inject(UiDialogService);
  month = inject(MonthService);

  private readonly session = toSignal(inject(SessionService).session$);
  readonly bucket = input.required<Bucket>();
  readonly bucketEdited = output<{ success: true }>();
  readonly bucketDeleted = output<{ success: true }>();

  formGroup = new FormGroup<{
    name: FormControl<string>;
    type: FormControl<BucketTypeOptionValue>;
    goal: FormControl<number | null>;
    amount: FormControl<number | null>;
    excludedFromSpendingReports: FormControl<boolean>;
  }>({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    type: new FormControl('BUDGET', { nonNullable: true, validators: [Validators.required] }),
    goal: new FormControl(0, Validators.pattern('[0-9.]*')),
    amount: new FormControl(0, Validators.pattern('[0-9.]*')),
    excludedFromSpendingReports: new FormControl(false, { nonNullable: true }),
  });
  readonly selectedType = signal<BucketTypeOptionValue>('BUDGET');
  readonly amountLabel = computed(() => bucketTypeAmountLabel(this.selectedType()));
  readonly goalLabel = computed(() => bucketTypeGoalLabel());
  readonly showsGoal = computed(() => bucketTypeShowsGoal(this.selectedType()));
  readonly updateBucketMutation = injectMutation(() => this.bucketService.updateBucketMutationOptions());
  readonly deleteBucketMutation = injectMutation(() => this.bucketService.deleteBucketMutationOptions());
  readonly archiveBucketMutation = injectMutation(() => this.bucketService.archiveBucketMutationOptions());
  readonly unarchiveBucketMutation = injectMutation(() => this.bucketService.unarchiveBucketMutationOptions());
  readonly saveError = signal<string | null>(null);
  readonly saveConfirmed = signal(false);
  private saveConfirmationTimer?: ReturnType<typeof setTimeout>;
  subscription = new Subscription();

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['bucket']) return;
    const previous = changes['bucket'].previousValue as Bucket | undefined;
    const bucket = this.bucket();
    if (bucket.archivedMonth) this.formGroup.disable({ emitEvent: false });
    else this.formGroup.enable({ emitEvent: false });
    if (!this.canChangeType) this.formGroup.controls.type.disable({ emitEvent: false });
    // Background refreshes of the same month must not erase an unsaved draft.
    if (previous?.id === bucket.id && previous.monthYear === bucket.monthYear) return;
    this.clearSaveConfirmation();
    this.saveError.set(null);
    const bucketType = bucket.type as BucketTypeOptionValue;
    this.selectedType.set(bucketType);
    this.formGroup.reset(
      {
        name: bucket.name,
        type: bucketType,
        goal: bucket.fund?.goal ?? null,
        amount: bucket.amount,
        excludedFromSpendingReports: bucket.type === 'TRANSFERS' || !!bucket.excludedFromSpendingReports,
      },
      { emitEvent: false },
    );
  }

  ngOnInit(): void {
    this.subscription.add(
      this.formGroup.controls.type.valueChanges.subscribe(type => {
        this.selectedType.set(type);
        if (type !== 'SAVINGS') this.formGroup.controls.goal.setValue(null, { emitEvent: false });
      }),
    );
  }

  get canChangeType(): boolean {
    const bucket = this.bucket();
    const zone = this.session()?.user.dateTimeZone;
    return (
      !!zone &&
      !bucket.archivedMonth &&
      bucket.type !== 'TRANSFERS' &&
      bucket.createdMonth === DateTime.local().setZone(zone).toFormat('yyyy-MM')
    );
  }

  changeType(type: BucketTypeOptionValue): void {
    if (!this.canChangeType) return;
    this.formGroup.controls.type.setValue(type);
    this.formGroup.controls.type.markAsDirty();
  }

  get loading(): boolean {
    return (
      this.updateBucketMutation.isPending() ||
      this.deleteBucketMutation.isPending() ||
      this.archiveBucketMutation.isPending() ||
      this.unarchiveBucketMutation.isPending()
    );
  }

  get saveButtonState(): UiSaveButtonState {
    if (this.updateBucketMutation.isPending()) return 'saving';
    if (this.formGroup.dirty) return 'ready';
    return this.saveConfirmed() ? 'success' : 'saved';
  }

  editBucketFormSubmit({ goal, name, amount, type, excludedFromSpendingReports }: EditBucketFormValue): void {
    const bucket = this.bucket();
    const nextGoal = bucketTypeShowsGoal(type) ? (goal ?? undefined) : undefined;
    if (
      bucket.type === type &&
      (bucket.fund?.goal ?? null) === (nextGoal ?? null) &&
      bucket.name === name &&
      bucket.amount === amount &&
      (bucket.type === 'TRANSFERS' || !!bucket.excludedFromSpendingReports === excludedFromSpendingReports)
    )
      return;
    if (this.formGroup.disabled || this.formGroup.invalid) return;
    this.clearSaveConfirmation();
    this.saveError.set(null);
    const submittedFormValue = this.formGroup.getRawValue();
    const editedBucket = toBucketUpdateDraft(bucket, {
      name,
      type: type as CreateBucketRequest['type'],
      amount: amount ?? 0,
      ...(bucket.type === 'TRANSFERS' ? {} : { excludedFromSpendingReports }),
      fund: { ...bucket.fund, goal: nextGoal },
    });

    this.updateBucketMutation.mutate(editedBucket, {
      onSuccess: () => {
        if (this.formValuesMatch(submittedFormValue)) {
          this.formGroup.markAsPristine();
          this.showSaveConfirmation();
        }
        this.analytics.sendEvent(EVENT_UPDATED_BUDGET);
      },
      onError: error => this.saveError.set(this.describeSaveError(error)),
    });
  }

  canDeleteBucket(bucket: Bucket): boolean {
    return bucket.createdMonth === this.month.monthYearFromDate() && bucket.transactions.totalRecords === 0;
  }

  confirmArchiveBucket(bucket: Bucket): void {
    this.dialog.open(ArchiveBucketDialogComponent, {
      width: '600px',
      data: { bucket, onSuccess: () => this.bucketEdited.emit({ success: true }) },
    });
  }

  deleteBucket(bucket: Bucket): void {
    this.deleteBucketMutation.mutate(bucket.id, {
      onSuccess: () => {
        this.analytics.sendEvent(EVENT_DELETED_BUDGET);
        this.bucketDeleted.emit({ success: true });
      },
    });
  }

  archiveBucket(bucket: Bucket): void {
    this.archiveBucketMutation.mutate(
      { bucketId: bucket.id },
      {
        onSuccess: () => {
          this.bucketEdited.emit({ success: true });
        },
      },
    );
  }

  unarchiveBucket(bucket: Bucket): void {
    this.unarchiveBucketMutation.mutate(bucket.id, {
      onSuccess: () => {
        this.bucketEdited.emit({ success: true });
      },
    });
  }

  ngOnDestroy(): void {
    this.clearSaveConfirmation();
    this.subscription.unsubscribe();
  }

  private formValuesMatch(submitted: EditBucketFormValue): boolean {
    const current = this.formGroup.getRawValue();
    return (
      current.name === submitted.name &&
      current.type === submitted.type &&
      current.goal === submitted.goal &&
      current.amount === submitted.amount &&
      current.excludedFromSpendingReports === submitted.excludedFromSpendingReports
    );
  }

  private showSaveConfirmation(): void {
    this.clearSaveConfirmation();
    this.saveConfirmed.set(true);
    this.saveConfirmationTimer = setTimeout(() => {
      this.saveConfirmed.set(false);
      this.saveConfirmationTimer = undefined;
    }, 700);
  }

  private clearSaveConfirmation(): void {
    if (this.saveConfirmationTimer) clearTimeout(this.saveConfirmationTimer);
    this.saveConfirmationTimer = undefined;
    this.saveConfirmed.set(false);
  }

  private describeSaveError(error: unknown): string {
    if (error instanceof Error && error.message) return error.message;
    return 'Unable to save budget settings. Try again.';
  }
}
